import { parsePickupWindows } from '@auctisync/api-client';
import { isoToZonedLocal, zonedLocalToIso } from '@auctisync/shared';

/**
 * Pure logic behind the collection scheduler on Admin → Auction.
 *
 * This is the writer the `pickup_dates` contract was written for. Migration
 * `20260902140000` documented the shape and said outright that "the scheduler
 * that will populate it belongs to AUCTION CREATION, because a collection
 * schedule is a per-sale, per-tenant choice that depends on the style of
 * auction" — and then nothing was built, so the column kept its default `[]`
 * on every row and `resolvePickup().nextWindow` was null everywhere.
 *
 * Kept out of the component for the same reason `brandingForm.ts` is: the rules
 * that decide what reaches the database get unit-tested without a DOM.
 *
 * 🔴 HYDRATION GOES THROUGH THE BIDDER'S OWN PARSER, deliberately. If this file
 * had its own reading of `pickup_dates`, an entry the admin form displayed
 * happily could be one `parsePickupWindows` drops — and the auctioneer would
 * believe they had scheduled a collection window that no bidder can see. The
 * form therefore shows exactly what the bidder's surfaces will show, because it
 * asks the same function.
 *
 * 🔴 EVERY CONVERSION TAKES THE SALE'S ZONE, never the browser's. Jim,
 * 2026-09-18, on the software he uses today: *"When I am traveling, I have to do
 * calculations to make sure I am entering the right auction times."* A
 * collection window typed as 9am means 9am at the warehouse gate; if this used
 * the machine's zone, the same typing would store a different instant depending
 * on where the auctioneer happened to be sitting. The arithmetic itself lives in
 * `@auctisync/shared` so the phone and the website cannot drift.
 */

/**
 * One row of the scheduler, holding what the auctioneer typed.
 *
 * ⚠️ THESE ARE `datetime-local` STRINGS IN THE SALE'S ZONE — not UTC, and not
 * the reader's zone either. The conversion happens at the edges so the middle
 * of the form never has to wonder which it is holding.
 */
export interface PickupWindowRow {
  /** Required. Blank on a row the auctioneer has only just added. */
  startsAt: string;
  /** Optional — an open-ended window is legal. */
  endsAt: string;
  /** Optional free text for this window only ("ring the bell at the yard gate"). */
  note: string;
}

export const EMPTY_WINDOW_ROW: PickupWindowRow = { startsAt: '', endsAt: '', note: '' };

/**
 * One entry as the column stores it. Mirrors the contract on the column itself.
 *
 * The index signature is what makes this assignable to the generated `Json`
 * type for `auctions.pickup_dates`, which is defined with one. It is preferred
 * to casting at the call site: a cast would also silence a genuine mismatch if
 * this shape and the contract ever parted company.
 */
export interface PickupDateEntry {
  starts_at: string;
  ends_at?: string;
  note?: string;
  [key: string]: string | undefined;
}

/** True when the auctioneer has not typed anything into this row yet. */
export function isBlankWindowRow(row: PickupWindowRow): boolean {
  return row.startsAt.trim() === '' && row.endsAt.trim() === '' && row.note.trim() === '';
}

/**
 * `auctions.pickup_dates` → editable rows in the sale's zone, earliest first.
 *
 * Anything the bidder's parser drops is dropped here too, so what the form lists
 * is what the sale actually has.
 */
export function windowRowsFromJson(raw: unknown, timeZone: string): PickupWindowRow[] {
  return parsePickupWindows(raw).map((w) => ({
    startsAt: isoToZonedLocal(w.startsAt, timeZone),
    endsAt: isoToZonedLocal(w.endsAt, timeZone),
    note: w.note ?? '',
  }));
}

/**
 * Editable rows → the JSON the column stores, as absolute UTC instants.
 *
 * Blank rows vanish (an added-but-unfilled row is not a schedule). Optional
 * keys are OMITTED rather than written as null, so a hand-read of the column
 * shows only what the house actually said. Sorted earliest-first to match what
 * the reader expects, so `nextPickupWindow` never has to re-sort.
 */
export function windowRowsToJson(
  rows: readonly PickupWindowRow[],
  timeZone: string,
): PickupDateEntry[] {
  const out: PickupDateEntry[] = [];

  for (const row of rows) {
    if (isBlankWindowRow(row)) continue;
    const startsAt = zonedLocalToIso(row.startsAt, timeZone);
    // Guarded by `validatePickupWindows` before a save is allowed, so this is
    // the belt to that braces: a row with no usable start is not a window and
    // must never be written with a substituted date.
    if (startsAt === null) continue;

    const entry: PickupDateEntry = { starts_at: startsAt };

    const endsAt = zonedLocalToIso(row.endsAt, timeZone);
    if (endsAt !== null && Date.parse(endsAt) > Date.parse(startsAt)) entry.ends_at = endsAt;

    const note = row.note.trim();
    if (note !== '') entry.note = note;

    out.push(entry);
  }

  return out.sort((a, b) => Date.parse(a.starts_at) - Date.parse(b.starts_at));
}

/** A problem with one row, addressed to the person who typed it. */
export interface PickupWindowError {
  /** Index into the rows array, so the form can mark the row that is wrong. */
  index: number;
  message: string;
}

/**
 * What the auctioneer must fix before this sale can be saved.
 *
 * 🔴 THE READER DROPS BAD ENTRIES SILENTLY; THE WRITER MUST NOT. That tolerance
 * is right on a bidder's phone — a malformed row costs a hidden button, never
 * an error page. It would be exactly wrong here: an auctioneer who typed an end
 * time before its start and saw the form accept it would walk away believing
 * collection was scheduled, and find out when a buyer turned up to a locked
 * yard. Same data, opposite failure direction, so the rules differ on purpose.
 */
export function validatePickupWindows(
  rows: readonly PickupWindowRow[],
  timeZone: string,
): PickupWindowError[] {
  const errors: PickupWindowError[] = [];

  rows.forEach((row, index) => {
    if (isBlankWindowRow(row)) return;

    const startsAt = zonedLocalToIso(row.startsAt, timeZone);
    if (startsAt === null) {
      errors.push({
        index,
        message: 'Give this collection time a start date and time, or clear the row.',
      });
      return;
    }

    const endsAt = zonedLocalToIso(row.endsAt, timeZone);
    if (endsAt !== null && Date.parse(endsAt) <= Date.parse(startsAt)) {
      errors.push({ index, message: 'This ends before it starts.' });
    }
  });

  return errors;
}
